import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Check, X, Loader2, AlertTriangle } from "lucide-react";
import { FieldLabel } from "@/components/wizard/FormKit";
import VoiceDictateButton from "@/components/wizard/VoiceDictateButton";
import SnippetPicker from "@/components/wizard/SnippetPicker";
import { useWizard } from "@/context/WizardContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_META = {
  polish: { label: "Fix grammar", hint: "Spelling, punctuation, and obvious grammar only. No content changes." },
  flag:   { label: "Check",       hint: "Flag inconsistencies with the rest of the chart. No rewriting." },
};

/**
 * Narrative field with inline AI assist.
 *   label, value, onChange  — like any textarea
 *   fieldKey                — stable id for Claude context (e.g. "cms485.goals_discharge")
 *   fieldPurpose            — short human description of what the form expects
 */
export default function NarrativeField({
  label, value, onChange, fieldKey, fieldPurpose = "",
  placeholder, testid, rows = 3,
}) {
  const { data } = useWizard();
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { text, notes, mode }

  const run = async (mode) => {
    setBusy(true);
    setSuggestion(null);
    try {
      const res = await axios.post(`${API}/ai/field-assist`, {
        field_key: fieldKey,
        field_label: label || fieldKey,
        field_purpose: fieldPurpose,
        current_text: value || "",
        mode,
        context: data,
      }, { timeout: 45000 });
      setSuggestion({ text: res.data.suggestion || "", notes: res.data.notes || "", mode });
    } catch (e) {
      toast.error(`AI assist failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const accept = () => {
    if (suggestion?.text && suggestion.mode === "polish") onChange(suggestion.text);
    setSuggestion(null);
    toast.success("Mechanical edits accepted. Verify the text reflects your clinical intent.");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
        {label && <FieldLabel>{label}</FieldLabel>}
        <div className="flex items-center gap-1 flex-wrap">
          <SnippetPicker
            fieldKey={fieldKey}
            testid={`${testid}-snippets`}
            onInsert={(text) => {
              const base = (value || "").trim();
              onChange(base ? `${base} ${text}` : text);
            }}
          />
          <VoiceDictateButton
            testid={`${testid}-mic`}
            label="Dictate"
            onResult={(text) => {
              const joined = value?.trim() ? `${value.trim()} ${text}` : text;
              onChange(joined);
            }}
          />
          {Object.entries(MODE_META).map(([m, meta]) => (
            <button
              type="button"
              key={m}
              disabled={busy}
              onClick={() => run(m)}
              title={meta.hint}
              className="text-[11px] font-semibold px-2.5 h-6 rounded-full bg-[#E7F1FB] border border-[#B7CADA] text-[#0A3D79] hover:bg-[#d9e6df] transition inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`${testid}-ai-${m}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {meta.label}
            </button>
          ))}
        </div>
      </div>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="tb-textarea"
        data-testid={testid}
      />
      {suggestion && (
        <div className="mt-2 rounded-lg border border-[#B7CADA] bg-[#E7F1FB]/60 p-3 fade-up" data-testid={`${testid}-ai-suggestion`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#0A3D79]">
              <Sparkles className="w-3 h-3" /> AI {MODE_META[suggestion.mode]?.label}
            </div>
            <div className="flex items-center gap-1">
              {suggestion.mode === "polish" && (
                <button onClick={accept} className="text-[12px] px-2 h-7 rounded-md bg-[#0A3D79] text-[#F7FAFC] font-semibold inline-flex items-center gap-1" data-testid={`${testid}-ai-accept`}>
                  <Check className="w-3 h-3" /> Accept edits
                </button>
              )}
              <button onClick={() => setSuggestion(null)} className="text-[12px] px-2 h-7 rounded-md bg-white border border-[#B7CADA] text-[#0B1E3A] inline-flex items-center gap-1" data-testid={`${testid}-ai-reject`}>
                <X className="w-3 h-3" /> {suggestion.mode === "flag" ? "Close" : "Discard"}
              </button>
            </div>
          </div>
          {suggestion.mode === "flag" ? (
            <div className="text-[13px] text-[#0B1E3A] flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#a07a27] mt-0.5 flex-shrink-0" />
              <span>{suggestion.notes || "No inconsistencies detected."}</span>
            </div>
          ) : (
            <>
              <div className="text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-1">Proposed edits (mechanical only — verify before accepting)</div>
              <div className="text-[13.5px] text-[#0B1E3A] whitespace-pre-wrap mb-2 bg-white rounded p-2 border border-[#D6E3EF]">{suggestion.text || "(no text returned)"}</div>
              {suggestion.notes && <div className="text-[11.5px] text-[#3A5275] italic">Changes: {suggestion.notes}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
